import {Signal, signal, WritableSignal} from "@angular/core";
import {
    PaymentAuthorisationType,
    WalletPaymentErrorType,
    WalletPaymentSubmissionFailure,
    WalletPaymentSubmissionResult,
    WalletProvider
} from '../payment';
import {PaymentModel} from "../payment.model";
import {ConfigurationModel, ParamConfig} from "../../configuration/configuration.model";
import {TyroPayRequestError} from "../tyro";
import {WalletPaymentProcessor} from "./wallet-payment-processor";

export class TyroWalletPaymentProcessor extends WalletPaymentProcessor {

    // Tyro renders its own wallet buttons directly rather than using the passive-button/quick-pay flow the
    // other providers use, so this never becomes true.
    public readonly quickPayAvailable: Signal<boolean> = signal<boolean>(false);

    private readonly _canSubmit: WritableSignal<boolean> = signal<boolean>(true);
    public readonly canSubmit: Signal<boolean> = this._canSubmit.asReadonly();

    private tyro: any;
    private payForm: any;
    private payFormHolder: HTMLElement;
    private payFormReady: boolean = false;

    constructor(paymentModel: PaymentModel, configurationModel: ConfigurationModel) {
        super(paymentModel, configurationModel);
    }

    public async init(): Promise<WalletPaymentProcessor> {
        this.payFormReady = false;
        if (!this.paymentModel.paymentPlatformPublicKey()) {
            return undefined;
        }

        console.log("Initialising Tyro for wallet payment. Live Mode =", this.paymentModel.paymentPlatformLiveMode());
        try {
            this.tyro = (<any>window).Tyro({
                liveMode: this.paymentModel.paymentPlatformLiveMode()
            });
            await this.tyro.init(this.paymentModel.paymentPlatformPublicKey());
            this.payForm = this.tyro.createPayForm({
                options: {
                    applePay: {
                        enabled: true
                    },
                    googlePay: {
                        enabled: true,
                        merchantInfo: {
                            merchantName: this.configurationModel.app()?.googlePayMerchantName,
                            merchantId: this.paymentModel.paymentPlatformLiveMode() ? this.configurationModel.app()?.googlePayMerchantId : undefined
                        }
                    },
                    creditCardForm: {
                        enabled: false
                    }
                }
            });

            // Inject the pay form into a "dummy" element.
            if (!this.payFormHolder) {
                const oldPayFormHolder: HTMLElement = document.querySelector("#tyro-wallet-pay-form-holder");
                if (oldPayFormHolder) {
                    document.body.removeChild(oldPayFormHolder);
                }
                this.payFormHolder = document.createElement("div");
                this.payFormHolder.id = "tyro-wallet-pay-form-holder";
                this.payFormHolder.style.display = "none";
                document.body.appendChild(this.payFormHolder);
            }
            if (this.payFormHolder.firstChild) {
                this.payFormHolder.removeChild(this.payFormHolder.firstChild);
            }
            this.payForm.setReadyListener(() => this.payFormReady = true);
            await this.payForm.inject("#tyro-wallet-pay-form-holder");
        } catch (error) {
            console.error("Tyro wallet payment initialisation failed", error);
            return undefined;
        }

        const paramConfig: ParamConfig = await this.paymentModel.configurationModel.getParamConfig();
        this._canSubmit.set(!paramConfig.forceTyroWalletButtons);

        await this.resolveWalletProvider();
        if (this.walletProvider() == undefined) {
            return undefined;
        }

        // noinspection JSUnusedLocalSymbols
        this.payForm.setWalletPaymentCompleteListener(async (paymentType: string, error: TyroPayRequestError) => {
            if (error) {
                this.rejectSubmission(new WalletPaymentSubmissionFailure(WalletPaymentErrorType.PROVIDER_ERROR, error.type, error.errorMessage, error.errorCode, error.gatewayCode));
                return;
            }

            void this.paymentModel.analyticsService.trackWalletPaymentRequestSucceeded(this.walletProvider());
            this.resolveSubmission(new WalletPaymentSubmissionResult(PaymentAuthorisationType.TYRO_PAYMENT_INTENT));
        });

        // noinspection JSUnusedLocalSymbols
        this.payForm.setWalletPaymentCancelledListener((paymentType: string) => {
            this.paymentModel.analyticsService.trackWalletPaymentRequestCancelled(this.walletProvider());
            this.rejectSubmission(new WalletPaymentSubmissionFailure(this.walletProvider() === WalletProvider.APPLE_PAY ? WalletPaymentErrorType.APPLE_PAY_CANCELLED : WalletPaymentErrorType.GOOGLE_PAY_CANCELLED));
        });
        return this;
    }

    /**
     * Tyro has no mechanism to detect whether wallet payment is configured on the device, so this uses the
     * Stripe API for that first, falling back to what Tyro itself can tell us. The two are not equally
     * authoritative though: Stripe's check can't rule wallet payment out (a negative may just mean the
     * card hasn't finished loading), so it's always "not sure yet" (null) - but Tyro's own check only ever
     * reports platform/hardware capability, never whether a wallet is actually configured with a card, so
     * a negative there is a definitive "not available" (undefined) that's never retried.
     */
    protected async determineWalletProvider(): Promise<WalletProvider | null | undefined> {
        let availability: any;
        let walletProvider: WalletProvider | null;
        if (this.configurationModel.app()?.stripeTestPublicKey) {
            try {
                availability = await new (<any>window).Stripe(this.configurationModel.app()?.stripeTestPublicKey).paymentRequest({
                    country: "AU",
                    currency: "aud",
                    total: {
                        label: "Order total",
                        amount: 100
                    },
                }).canMakePayment();
                if (availability) {
                    walletProvider = availability.applePay ? WalletProvider.APPLE_PAY
                        : availability.googlePay ? WalletProvider.GOOGLE_PAY
                            : null;
                }
            } catch (error) {
                console.error("Failed to check wallet availability using Stripe", error);
            }
        }

        // Fall back to checking with Tyro (device/platform capability only - definitive either way).
        if (!availability) {
            try {
                return this.payForm.isApplePaySupported() ? WalletProvider.APPLE_PAY
                    : this.payForm.isGooglePaySupported() ? WalletProvider.GOOGLE_PAY
                        : undefined;
            } catch (error) {
                console.error("Failed to check wallet availability using Tyro", error);
                return null;
            }
        }

        return walletProvider;
    }

    /**
     * Tyro's canSubmit additionally depends on the payForm API's shape (whether it supports the
     * passive-button invocation pattern), not just walletProvider - re-evaluated here whenever
     * walletProvider changes, including a later background recheck, since a provider found late still
     * needs this same narrowing applied.
     */
    protected override onWalletProviderChanged(): void {
        const walletProvider: WalletProvider = this.walletProvider();
        if (walletProvider !== undefined && this._canSubmit() && (
            (walletProvider === WalletProvider.APPLE_PAY && !this.payForm.applePayButton.onApplePayButtonClicked) ||
            (walletProvider === WalletProvider.GOOGLE_PAY && !this.payForm.applePayButton.onApplePayButtonClicked))) {
            console.log("Using Tyro wallet payment buttons - API version is incompatible with passive button mode.");
            this._canSubmit.set(false);
        }
    }

    // noinspection JSUnusedLocalSymbols
    public async showPaymentSheet(amount: number): Promise<void> {
        if (!this.payFormReady) {
            this.rejectSubmission && this.rejectSubmission(new WalletPaymentSubmissionFailure(WalletPaymentErrorType.PROVIDER_NOT_READY));
            return;
        }

        // payForm.googlePayButton.onGooglePayButtonClicked and
        // payForm.applePayButton.onApplePayButtonClicked are undocumented APIs that allow us
        // to handle Tyro wallet payments the same way we do for Stripe.
        // i.e. with a passive button to start the order placement flow, and invocation of the wallet payment
        // only after validations and other async processing has completed.
        if (this.walletProvider() === WalletProvider.APPLE_PAY) {
            this.payForm.applePayButton.onApplePayButtonClicked();
        } else if (this.walletProvider() === WalletProvider.GOOGLE_PAY) {
            this.payForm.googlePayButton.onGooglePayButtonClicked();
        }
    }
}
